// components
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import {
  StyledLottieContainer,
  StyledLottieFrame,
  StyledSettingsList
} from './FlexMatchDrawerSettings.styles';
import { SortableStepRow } from './SortableStepRow';

// hooks
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';

// types
import { FlexMatchDrawerSettingsProps } from './FlexMatchDrawerSettings.types';

// utils
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Typography } from '@mui/material';
import { t } from 'i18next';

export const FlexMatchDrawerSettings = ({
  filterSteps,
  currentStep,
  onReorderSteps,
  onDeleteStep,
  onStartEditing
}: FlexMatchDrawerSettingsProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    onReorderSteps(active.id, over.id);
  };

  const lottieUrl = 'aysist/styles/external/lottie/emptyBoxAnimation.lottie';

  return filterSteps.length > 0 ? (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={filterSteps.map((step) => step.id!)}
        strategy={verticalListSortingStrategy}
      >
        <StyledSettingsList>
          {filterSteps.map((step) => (
            <SortableStepRow
              key={step.id}
              step={step}
              isCurrent={currentStep?.id === step.id}
              onSelect={() => {
                onStartEditing(step);
              }}
              onDelete={() => onDeleteStep(step.id!)}
            />
          ))}
        </StyledSettingsList>
      </SortableContext>
    </DndContext>
  ) : (
    <StyledLottieContainer>
      <StyledLottieFrame>
        <DotLottieReact
          src={window?.reactMetadata?.staticUrl + lottieUrl}
          loop
          autoplay
        />
        <Typography variant="body1" color="textSecondary">
          {t('flexMatch.youHaveNoSteps')}
        </Typography>
      </StyledLottieFrame>
    </StyledLottieContainer>
  );
};
